import { Button, Flex } from "@mantine/core";
import { IconFileSpreadsheet } from "@tabler/icons-react";
import { reportApi } from "apis/reportApi";
import CreatableSelect from "components/ui/CreatableSelect/CreatableSelect";
import DateRangeInput, {
  DateRangeValue,
} from "components/ui/DateRangeInput/DateRangeInput";
import GroupButton, {
  ActionButton,
} from "components/ui/GroupButton/GroupButton";
import dayjs from "configs/dayjs";
import {
  SALES_ORDER_STATUS,
  SALES_ORDER_STATUS_LABEL,
} from "constants/common/salesOrder";
import { REPORT_TAB } from "constants/common/report";
import { ReportParamsType } from "constants/types/common";
import { FC, useMemo, useState } from "react";
import { useNotification } from "utils/hooks";
import { useCustomers, useSuppliers } from "utils/hooks/request/usePartners";

const getDefaultDateRange = (): DateRangeValue => [
  dayjs().startOf("month").toDate(),
  dayjs().endOf("month").toDate(),
];

const STATUS_OPTIONS = Object.values(SALES_ORDER_STATUS).map((value) => ({
  value,
  label: SALES_ORDER_STATUS_LABEL[value],
}));

interface ReportFiltersProps {
  onApply: (params: ReportParamsType) => void;
  /** Tab đang mở — quyết định xuất báo cáo nào */
  activeTab: string;
}

const ReportFilters: FC<ReportFiltersProps> = ({ onApply, activeTab }) => {
  const { customerOptions, isLoading: loadingCustomers } = useCustomers();
  const { supplierOptions, isLoading: loadingSuppliers } = useSuppliers();
  const { notification } = useNotification();
  // Tab mua hàng chạy trên đơn MUA: đối tác là nhà cung cấp, và bộ lọc trạng
  // thái ở đây là enum của đơn bán (pending/confirmed/...) nên không dùng được —
  // đơn mua chỉ có draft | completed, gửi lên sẽ bị backend từ chối.
  const isPurchase = activeTab === REPORT_TAB.PURCHASE;
  const [dateRange, setDateRange] =
    useState<DateRangeValue>(getDefaultDateRange);
  const [partnerId, setPartnerId] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [dateError, setDateError] = useState<string | undefined>();
  const [isExporting, setIsExporting] = useState(false);

  const partnerSelectOptions = useMemo(
    () => (isPurchase ? supplierOptions : customerOptions) ?? [],
    [isPurchase, supplierOptions, customerOptions],
  );

  const handleDateChange = (range: DateRangeValue) => {
    setDateRange(range);
    const [start, end] = range;
    if (start && end && dayjs(start).isAfter(dayjs(end))) {
      setDateError("Ngày bắt đầu phải trước ngày kết thúc");
    } else {
      setDateError(undefined);
    }
  };

  const getFilterParams = (): ReportParamsType | null => {
    const [start, end] = dateRange;
    if (!start || !end) {
      setDateError("Vui lòng chọn khoảng thời gian");
      return null;
    }
    return {
      startDate: dayjs(start).startOf("day").unix(),
      endDate: dayjs(end).endOf("day").unix(),
      partnerId: partnerId ?? undefined,
      status: isPurchase ? undefined : (status ?? undefined),
    };
  };

  const handleApply = () => {
    const params = getFilterParams();
    if (!params) return;
    onApply(params);
  };

  const handleExportExcel = async () => {
    if (isExporting) return;
    const params = getFilterParams();
    if (!params) return;

    setIsExporting(true);
    try {
      // Mỗi tab có mẫu Excel riêng
      const exporters: Record<
        string,
        { call: typeof reportApi.exportProfitReport; fileName: string }
      > = {
        [REPORT_TAB.COLLECTION]: {
          call: reportApi.exportCollectionReport,
          fileName: "tinh-hinh-thu-tien.xlsx",
        },
        [REPORT_TAB.PURCHASE]: {
          call: reportApi.exportPurchaseReport,
          fileName: "tinh-hinh-mua-hang.xlsx",
        },
      };
      const exporter = exporters[activeTab] ?? {
        call: reportApi.exportProfitReport,
        fileName: "bao-cao-loi-nhuan.xlsx",
      };

      const res = await exporter.call(params);
      const url = URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = exporter.fileName;
      link.click();
      URL.revokeObjectURL(url);
      notification.success({ title: "Xuất Excel thành công" });
    } catch {
      notification.error({ title: "Xuất Excel thất bại" });
    } finally {
      setIsExporting(false);
    }
  };

  const isInvalid = !dateRange[0] || !dateRange[1] || !!dateError;
  const actions: ActionButton[] = [
    {
      key: "export-excel",
      label: "Xuất Excel",
      icon: <IconFileSpreadsheet size={18} />,
      variant: "filled",
      color: "#217346",
      isLoading: isExporting,
      disabled: isExporting || isInvalid,
      onClick: handleExportExcel,
      permission: "report:read",
    },
  ];

  return (
    <Flex gap="sm" align="flex-end" wrap="wrap">
      <DateRangeInput
        label="Khoảng thời gian"
        value={dateRange}
        onChange={handleDateChange}
        error={dateError}
        w={280}
      />
      <CreatableSelect
        label={isPurchase ? "Nhà cung cấp" : "Khách hàng"}
        placeholder={
          isPurchase ? "Tất cả nhà cung cấp" : "Tất cả khách hàng"
        }
        data={partnerSelectOptions}
        value={partnerId}
        onChange={setPartnerId}
        allowCreate={false}
        clearable
        disabled={isPurchase ? loadingSuppliers : loadingCustomers}
        w={240}
      />
      {!isPurchase && (
        <CreatableSelect
          label="Trạng thái"
          placeholder="Tất cả trạng thái"
          data={STATUS_OPTIONS}
          value={status}
          onChange={setStatus}
          allowCreate={false}
          clearable
          w={200}
        />
      )}
      <Button onClick={handleApply} disabled={isInvalid}>
        Áp dụng
      </Button>

      <GroupButton actions={actions} />
    </Flex>
  );
};

export default ReportFilters;
